import type { TFunction } from 'i18next';
import { relativeAgeBucket } from '@/lib/relativeTime';
import { STALE_AFTER_SEC } from './motion';
import type { VehicleState, VehicleStatus } from './types';

/**
 * ONE VOCABULARY (slice 13e). The product used to speak FIVE overlapping status
 * vocabularies, and the same vehicle read "Online" on the list and "Unknown" on
 * the map at the same moment — both correct, both confusing. This module is the
 * single source of the three NON-OVERLAPPING vocabularies that replace them, so no
 * two facts ever share a word (Q19 / "Unknown is the default state of a parked
 * vehicle"):
 *
 *   1. VEHICLE STATE — what the vehicle is doing (`state`). Prints a word only for
 *      the four states that make an honest claim; `noContact`/`unknown` print NO
 *      word — a vehicle we cannot characterise gets FACTS, not a label.
 *   2. TRACKER STATE — is the tracker reporting (`status`, re-worded so it shares
 *      no word with vehicle state): online → Reporting, offline → Not reporting,
 *      unknown → render nothing (never invent a claim).
 *   3. POSITION AGE — always a measurement, never a state word: "Position {age}
 *      old", plus "No satellite view" when the last fix saw zero satellites.
 *
 * Everything here is PURE — no React, no JSX — so the rules that decide which
 * words a surface may honestly show are unit-tested without a browser. The
 * component (`VehicleStateLine.tsx`) renders what these functions return and
 * invents nothing.
 *
 * 🔴 The word "Unknown" is NEVER produced here as a vehicle status. `unknown` and
 * `noContact` resolve to measurements; the summary/chip labels for them use a
 * measurement/tracker framing, never the bare word.
 */

const VEHICLE_STATES: readonly VehicleState[] = [
  'moving',
  'idling',
  'parked',
  'engineOn',
  'engineStale',
  'noContact',
  'unknown',
];

/** Softens a wire value to a known `VehicleState`. Anything absent or unrecognised
 *  becomes `unknown` — the honest default for a vehicle we cannot characterise, and
 *  the state the API itself emits when ignition is unknown (RAAD-TEST-01 forever). */
export function normalizeVehicleState(value: unknown): VehicleState {
  return typeof value === 'string' && (VEHICLE_STATES as readonly string[]).includes(value)
    ? (value as VehicleState)
    : 'unknown';
}

/**
 * The four states that make an honest vehicle-state CLAIM and therefore print a
 * word. `noContact`/`unknown` do NOT — a surface renders measurements for them and
 * never a state word. This is a `Record<VehicleState, boolean>` so a seventh state
 * is a compile error (a missing key) rather than a silent `false`.
 */
export const STATE_HAS_LABEL: Record<VehicleState, boolean> = {
  moving: true,
  idling: true,
  parked: true,
  engineOn: true,
  // 15e. engineStale DOES print a word, and that is the point: "Engine state
  // unknown" NAMES what is unknown. It is not in the label-less family with
  // `unknown`/`noContact`, which decline to characterise the vehicle at all —
  // here we know precisely what we do not know, and say so.
  engineStale: true,
  noContact: false,
  unknown: false,
};

export function stateHasLabel(state: VehicleState): boolean {
  return STATE_HAS_LABEL[state];
}

/**
 * The visual tone each state carries. `engineOn` is the one Mahad cares most about
 * (ignition on, fix stale — the vehicle could be moving where we cannot see it), so
 * it takes the warn family to STAND OUT; it is a LABEL, not an alert. `moving` is
 * business green; `idling`/`parked` are neutral slate. `noContact`/`unknown` never
 * print a word, so their tone is only used by the chip/summary labels (muted).
 * A `Record<VehicleState, …>` — a seventh state fails `tsc` here.
 */
export type StateTone =
  | 'moving'
  | 'idling'
  | 'parked'
  | 'engineOn'
  | 'trackerOn'
  | 'trackerOff'
  | 'measure';

export const STATE_TONE: Record<VehicleState, StateTone> = {
  moving: 'moving',
  idling: 'idling',
  parked: 'parked',
  engineOn: 'engineOn',
  // 15e. `measure`, deliberately NOT `engineOn`'s warn amber. engineOn is amber
  // because it MIGHT mean "moving where we cannot see it" — a positive claim worth
  // a look. engineStale makes no claim at all, and dressing an admission of
  // uncertainty in an attention colour would read as an alert about a vehicle we
  // know nothing about.
  engineStale: 'measure',
  noContact: 'measure',
  unknown: 'measure',
};

// 🔴 MOVED to `@raad/live-markers` (slice 84): the PURE state rank/order/count/sort
// (`VEHICLE_STATE_ORDER`, `StateCounts`, `countByState`, `sortByState`) so the staff
// Global-live list orders and counts a fleet IDENTICALLY to the tenant list. The
// i18n-coupled rest of this module (buildStatusLine, chips, relativeTime) stays here.
// Imported for local use (chipsCanFilter/visibleChipStates below) AND re-exported so
// every existing importer is unchanged.
import { VEHICLE_STATE_ORDER, countByState, sortByState, type StateCounts } from '@raad/live-markers';
export { VEHICLE_STATE_ORDER, countByState, sortByState };
export type { StateCounts };

/** The last fix saw NO satellites — the exact "parked under cover" tell. A `null`
 *  sat count means we hold no reading and must claim nothing; only an explicit 0
 *  earns the "No satellite view" note. */
export function hasNoSatelliteView(sats: number | null | undefined): boolean {
  return sats === 0;
}

// ── The one-line status model ────────────────────────────────────────────────

/** The facts a surface hands the vocabulary. `speedKmh` is present only where the
 *  surface holds it (the live map/detail); the list has no speed and omits it. */
export interface StatusFacts {
  state: VehicleState;
  status: VehicleStatus;
  positionAgeSec: number | null;
  /** Satellites in the last fix (from `trackerHealth.sats`); 0 ⇒ "No satellite view". */
  sats: number | null;
  /** km/h, already converted server-side. Optional — only the label-less states use it. */
  speedKmh?: number | null;
  /** 15e seam — server-derived: was the last stored fix at/above the moving
   *  threshold? Chooses `engineStale`'s sub-line. Null ⇒ no sub-line. */
  lastFixMoving?: boolean | null;
}

export interface StatusSegment {
  key: string;
  text: string;
  tone: StateTone;
}

export interface StatusLine {
  /** Ordered segments, rendered joined by " · " — vehicle-state label (when the
   *  state makes a claim), then tracker state, then speed (label-less states only),
   *  then the position-age measurement. */
  segments: StatusSegment[];
  /** The "No satellite view" note, appended after the segments with " — ". Null
   *  unless the last fix saw zero satellites. */
  satNote: string | null;
}

/** The bare age DURATION ("19 h", "2 min"), never "19 h ago" — the position-age
 *  copy reads "Position 19 h old", so it needs a duration, not a relative phrase. */
function ageDuration(t: TFunction, ageSec: number): { justNow: boolean; text: string } {
  const bucket = relativeAgeBucket(ageSec);
  if (bucket.key === 'justNow') return { justNow: true, text: '' };
  return { justNow: false, text: t(`vehicleState.age.${bucket.key}`, { count: bucket.count }) };
}

/**
 * Build the one-line status from the facts. The canonical line is
 *
 *   Parked · Reporting · Position 19 h old — no satellite view
 *
 * and the composition is:
 *   1. vehicle-state label — ONLY for the four states that make a claim;
 *   2. tracker state — Reporting / Not reporting; nothing when `status` is unknown;
 *   3. speed — ONLY for the label-less states (`unknown`/`noContact`) that carry a
 *      speed, so the no-ignition phone case reads as a "speed-and-age statement";
 *   4. position age — "Position {age} old", or "Position just updated" when fresh.
 *
 * "Unknown" is never produced. When `state` is `unknown`/`noContact` there is no
 * step-1 label, so the line leads with the measurements — facts, not a word.
 */
export function buildStatusLine(
  t: TFunction,
  facts: StatusFacts,
  opts: { omitState?: boolean; omitTracker?: boolean; omitSat?: boolean } = {},
): StatusLine {
  const segments: StatusSegment[] = [];

  // 1. Vehicle-state label — only when the state makes an honest claim, and only
  //    when the surface is not already showing it as a pill (`omitState`), so the
  //    sheet/detail never print the state word twice.
  if (!opts.omitState && stateHasLabel(facts.state)) {
    segments.push({
      key: 'state',
      text: t(`vehicleState.${facts.state}`),
      tone: STATE_TONE[facts.state],
    });
  }

  // 1b. 15e — the engineStale sub-line. The line reads
  //     "Engine state unknown · Last seen driving 17 min ago · Reporting · …",
  //     naming the uncertainty and then supplying the one fact we actually hold.
  //
  //     🔴 ONE STATE, TWO SUB-LINES, and the choice is NOT cosmetic. A vehicle last
  //     seen stationary described as "Last seen driving 17 min ago" is exactly the
  //     confident wrong sentence this state exists to remove — one state with the
  //     wrong sub-line is still a false claim. So:
  //
  //       lastFixMoving === true   → "Last seen driving {{age}} ago"
  //       lastFixMoving === false  → "Last seen with engine on {{age}} ago"
  //       lastFixMoving === null   → NO sub-line. We hold no speed reading, so we
  //                                  can claim neither; the state word stands alone.
  //
  //     🔴 THE DISCRIMINATOR IS SERVER-DERIVED and must stay that way. Deriving it
  //     here from `speedKmh` would work on the map and silently pick the wrong
  //     string on /vehicles, which carries no speed by design (slice 06) — the same
  //     fact worded two ways on two screens, the exact defect 13e ended.
  //
  //     THE AGE IS THE POSITION AGE: the sub-line describes the last stored FIX, so
  //     that fix's age is precisely "how long ago we last saw it".
  //
  //     Suppressed when the surface omits the state word (`omitState`): without
  //     "Engine state unknown" in front of it, a bare "Last seen driving 17 min ago"
  //     reads as a claim about what the vehicle is doing NOW.
  if (
    !opts.omitState &&
    facts.state === 'engineStale' &&
    facts.positionAgeSec != null &&
    facts.lastFixMoving != null
  ) {
    const d = ageDuration(t, facts.positionAgeSec);
    if (!d.justNow) {
      segments.push({
        key: 'engineStaleSince',
        text: t(
          facts.lastFixMoving
            ? 'vehicleState.engineStaleSinceDriving'
            : 'vehicleState.engineStaleSinceEngineOn',
          { age: d.text },
        ),
        tone: 'measure',
      });
    }
  }

  // 2. Tracker state. `unknown` prints NOTHING — never an invented claim. Omitted
  //    on surfaces that show the dedicated tracker-health block (the sheet, the
  //    detail Live tab), so the tracker word is never printed twice.
  if (!opts.omitTracker) {
    if (facts.status === 'online') {
      segments.push({ key: 'tracker', text: t('trackerState.online'), tone: 'trackerOn' });
    } else if (facts.status === 'offline') {
      segments.push({ key: 'tracker', text: t('trackerState.offline'), tone: 'trackerOff' });
    }
  }

  // 3. Speed — for a MOVING vehicle (its live speed, always fresh by construction),
  //    and for the label-less states ONLY when the fix is FRESH: that is the
  //    no-ignition phone case (state `unknown`, recent fix), read honestly as a
  //    speed-and-age statement. A STALE unknown/noContact fix carries a stale speed
  //    that is not the current speed, so it is never shown (the same rule the map
  //    marker uses). Never for idling/parked/engineOn, where speed is 0 or moot.
  const fixFresh = facts.positionAgeSec != null && facts.positionAgeSec <= STALE_AFTER_SEC;
  const showSpeed =
    facts.speedKmh != null &&
    (facts.state === 'moving' || (!stateHasLabel(facts.state) && fixFresh));
  if (showSpeed) {
    segments.push({
      key: 'speed',
      text: t('livemap.pill.speed', { speed: Math.round(facts.speedKmh as number) }),
      tone: 'measure',
    });
  }

  // 4. Position age — always a measurement.
  if (facts.positionAgeSec != null) {
    const d = ageDuration(t, facts.positionAgeSec);
    segments.push({
      key: 'age',
      text: d.justNow ? t('vehicleState.positionJustNow') : t('vehicleState.positionAge', { age: d.text }),
      tone: 'measure',
    });
  }

  // The "No satellite view" tell. Omitted where the tracker-health block already
  // shows the satellite count (the sheet / detail), so it is not stated twice.
  const satNote =
    !opts.omitSat && hasNoSatelliteView(facts.sats) ? t('vehicleState.noSatellite') : null;
  return { segments, satNote };
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * WHICH CHIPS RENDER (slice 17t). MEASURED, not a taste call.
 * ─────────────────────────────────────────────────────────────────────────────
 * All seven chips at 360 px measure 638 px of content in a 360 px box — 278 px
 * off the right edge, the last chip unreachable. In Somali it is worse: 666 px,
 * 306 px off-screen, and a 93 px band instead of 77 px. So the row must be cut,
 * and the cut has to be principled or it just moves the problem.
 *
 * ALWAYS — the three that describe a working fleet, so the summary is stable and
 * a customer learns where to look:
 *   moving · engineOn · parked
 *
 * ONLY WHEN NON-ZERO — the exception states. A permanent zero is furniture a
 * customer learns to ignore; appearing the moment it is non-zero puts it in front
 * of them exactly when it means something (Mahad, 30 Aug):
 *   noContact · engineStale · unknown · idling
 *
 * 🔴 AND A HARD CAP OF FOUR, because "always 3 + up to 4 conditional" is 7 again
 * on a bad day — the exact overflow this rule exists to remove. When more than one
 * exception is live, `EXCEPTION_PRIORITY` decides which one gets the slot.
 *
 * 🔴 `noContact` IS FIRST IN THAT PRIORITY AND MUST STAY FIRST. It is the state a
 * stolen tracker, a dead SIM or a cut wire produces — Mahad, 30 Aug: "the one I
 * most need to see when it happens". Dropping it in favour of a data-quality state
 * would hide the only chip that means someone must act. Measured: it is also the
 * most common exception, 45.2% of 22 days on KN654.
 *
 * The states NOT shown are not hidden from the customer — every vehicle's own row
 * carries its state in words. This governs the FILTER row only.
 */
const ALWAYS: readonly VehicleState[] = ['moving', 'engineOn', 'parked'];

/** Which exception wins the fourth slot. Order is meaning, not frequency. */
/**
 * Which exception wins the fourth slot. Order is meaning, not frequency.
 *
 * IDLING IS DELIBERATELY ABSENT, so its chip never renders (Mahad, 30 Aug).
 * NOT because the state is broken: it fires — 31 times on KN654 in 22 days — and
 * the data is real, 12h 24m of it, of which 97.4% is measured genuine
 * engine-on-not-moving.
 *
 * It is absent because A CHIP IS AN ANSWER AND THERE ARE TWO ANSWERS. This surface
 * would key on SPEED_MOVING_KMH (5, reported speed) while the trips screen counts
 * idle by IDLE_SPEED_KMH (3, displacement). Between 3 and 5 km/h they disagree —
 * a vehicle at 4 km/h reads "Engine on" here and "moving" there — and those are the
 * two screens a customer compares in one session.
 *
 * Restore it when ONE derivation exists. See BOARD.md, "the live view and the trip
 * record can disagree about whether a vehicle was moving". Do NOT restore it by
 * aligning the two constants: neither was ever measured against a vehicle — 5 was
 * inherited from the engine-cut safety threshold for a cosmetic purpose, and 3 has
 * no recorded justification at all.
 */
const EXCEPTION_PRIORITY: readonly VehicleState[] = ['noContact', 'engineStale', 'unknown'];

const MAX_CHIPS = 4;

/**
 * Whether the chip row is worth rendering AT ALL — a filter earns its place when
 * it can change what you see, and with one vehicle nothing can.
 *
 * 🔴 ONE derivation, used by BOTH surfaces: the desktop left panel and the mobile
 * top bar. They previously each decided for themselves whether to show chips, and
 * two implementations of one rule is the shape this repo keeps paying for.
 */
export function chipsCanFilter(counts: StateCounts): boolean {
  return VEHICLE_STATE_ORDER.filter((s) => (counts[s] ?? 0) > 0).length >= 2;
}

/**
 * The chips to render, in `VEHICLE_STATE_ORDER` so the row never reshuffles under
 * the reader's finger — priority decides WHICH exception appears, never WHERE.
 */
export function visibleChipStates(counts: StateCounts): VehicleState[] {
  const exception = EXCEPTION_PRIORITY.filter((s) => (counts[s] ?? 0) > 0).slice(0, MAX_CHIPS - ALWAYS.length);
  const chosen = new Set<VehicleState>([...ALWAYS, ...exception]);
  return VEHICLE_STATE_ORDER.filter((s) => chosen.has(s));
}
